import React, { useState, useEffect, useRef } from "react";
import * as signalR from "@microsoft/signalr";
import { useToken } from '../components/items/UseToken';
import { OpusDecoder } from "opus-decoder";
import { Button, IconButton, Tooltip, Box } from "@mui/material";
import MicIcon from "@mui/icons-material/Mic";
import StopIcon from "@mui/icons-material/Stop";
import SendIcon from "@mui/icons-material/Send";


function getChatbotHubUrl() {
    const { protocol, hostname } = window.location;

    // DEV: localhost lub 127.0.0.1
    if (hostname.includes('localhost') || hostname.startsWith('127.')) {
        return 'http://localhost:32983/chatbothub';
    }

    const parts = hostname.split('.');
    const baseDomain = parts.slice(0, -1).join('.');
    return `https://assistant.${baseDomain}.app/chatbothub`;
}

export const publicChatbotHub = getChatbotHubUrl();

export default function ChatWindow({ selectedAgent, notificationRef }) {
    const publicChatbotHub = getChatbotHubUrl();
    const clusterChatbotHub = 'https://ai-assistant.default.svc.cluster.local/chatbothub';
    const localChatbotHub = 'http://localhost:5000/chatbothub';
    const audioSourceRef = useRef(null);

    const generateUUID = () => crypto.randomUUID();
    const [chatbotHubUrl, setChatbotHubUrl] = useState(publicChatbotHub);
    const [sessionId, setSessionId] = useState(null);
    const [machines, setMachines] = useState([]);
    const [connectedComputers, setConnectedComputers] = useState([]);
    const [messages, setMessages] = useState({});
    const [imagesAtt, setImagesAtt] = useState([]);
    const [isConnected, setIsConnected] = useState(false);
    const messageInputRef = useRef();
    const chatMessagesRef = useRef();
    const connection = useRef(null);
    const { token } = useToken();
    const [selectedImage, setSelectedImage] = useState(null); // State for modal image
    const [displayImages, setDisplayImages] = useState(true);

    const [isRecording, setIsRecording] = useState(false);
    const mediaRecorderRef = useRef(null);
    const audioContextRef = useRef(null);
    const audioBufferQueueRef = useRef([]); // Kolejka buforów audio
    const isPlayingRef = useRef(false); // Flaga odtwarzania


    const fileInputRef = useRef(null);

    const handleImageChange = async (e) => {
        const files = Array.from(e.target.files);
        const promises = files.map(file => {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => {
                    resolve({
                        base64: reader.result,   // 🟣 This is your actual base64 string
                        url: URL.createObjectURL(file) // optional for preview
                    });
                };
                reader.onerror = reject;
                reader.readAsDataURL(file); // ✅ Important: read as Data URL
            });
        });

        const newBase64Images = await Promise.all(promises);
        setImagesAtt([...imagesAtt, ...newBase64Images]);
    };

    const handleRemoveImage = (index) => {
        const updated = [...imagesAtt];
        updated.splice(index, 1);
        setImagesAtt(updated);
    };

    const handleImageClick = (imageSrc) => {
        setSelectedImage(imageSrc); // Set the selected image for
    };

    const closeModal = () => {
        setSelectedImage(null); // Close modal
    };

    // Initialize SignalR connection
    useEffect(() => {
        connection.current = new signalR.HubConnectionBuilder()
            .withUrl(publicChatbotHub)
            .build();

        connection.current.start()
            .then(() => {
                console.log("SignalR connected");
                setIsConnected(true);
                startSession(selectedAgent.guid);
            })
            .catch((err) => {
                console.error("SignalR connection error:", err);
            });

        connection.current.on("ReceiveMessage", (response, messageType) => {
            console.log("Received Message:", response);
            if (messageType === "barge") {
                bargeIn();
            }
            else {
                addMessageToChat(response, messageType);
            }
            
        });

        connection.current.on("ReceiveAudio", async (incomingAudio) => {
            console.log("Audio received from bot " + incomingAudio);
            handleIncomingChunk(incomingAudio);
        });
        
        fetchMachine();


        return () => {
            
            connection.current.stop();
            setIsConnected(false);
            
           
            console.log("SignalR disconnected");
        };
    }, [selectedAgent]);

    // Start session when SignalR is connected and agent is selected
    useEffect(() => {
        if (isConnected && selectedAgent) {
            
        }
    }, [isConnected]);

    const startSession = async (agentGuid) => {
        try {
            const newSessionId = generateUUID();
            const agentPayload = selectedAgent;
            await connection.current.invoke("StartSession", newSessionId, agentPayload, false);
            console.log(`Session started for agent: ${agentGuid}`);

            setSessionId(newSessionId); // Set session ID
            setMessages((prev) => ({
                ...prev,
                [agentGuid]: prev[agentGuid] || [],
            }));
        } catch (error) {
            console.error("Error starting session:", error);
        }
    };

    const connectComputer = async () => {
        var payload = JSON.stringify({ machineKey: machines[0].guid, sessionId: sessionId, agentUrl: chatbotHubUrl });
        await connection.current.invoke("RequestComputer", payload, window.location.origin.toString() + "/api/requestmachineconnection", token);
    }

    const fetchMachine = async () => {
        

        const query = "api/getmachines?agent_id=" + selectedAgent.guid;

        try {
            const response = await fetch(query, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: "Bearer " + token,
                },
            });

            if (!response.ok) {
                throw new Error("Failed to fetch machines");
            }

            const data = await response.json();
            setMachines(data.results || []); // Assuming data contains `results`
        } catch (err) {
            console.error("Error fetching machines:", err);
        } 
    };

    const sendMessage = async () => {
        const message = messageInputRef.current.value.trim();
        if (message && sessionId) {
            setMessages((prev) => ({
                ...prev,
                [selectedAgent.guid]: [
                    ...(prev[selectedAgent.guid] || []),
                    { message, type: "sent" },
                ],
            }));

            messageInputRef.current.value = "";

            try {
                if (imagesAtt.length > 0) {
                    const payload = imagesAtt.map(img => img.base64);
                    console.log(payload);
                    await connection.current.invoke("SendMessageWithImages", sessionId, message, payload);

                }
                else {
                    await connection.current.invoke("SendMessage", sessionId, message);

                }
                setImagesAtt([]);
            } catch (error) {
                console.error("Failed to send message:", error);
            }
        }
    };

    

    const addMessageToChat = (message, messageType) => {
        if (selectedAgent && (messageType === "received" || messageType === "sent" || (messageType === "image" && displayImages))) {
            setMessages((prev) => ({
                ...prev,
                [selectedAgent.guid]: [
                    ...(prev[selectedAgent.guid] || []),
                    { message, type: messageType },
                ],
            }));
            hideMessage();
        }
        if (messageType === "log") {
            showMessage(message);
        }
    };
    // Function to add a message to the chat
    

    const showMessage = (message) => {
        const notification = notificationRef.current;
        if (notification) {
            notification.textContent = message;
            notification.classList.remove('hidden', 'fade-out');
        }
    };

    const hideMessage = () => {
        const notification = notificationRef.current;
        if (notification) {
            notification.classList.add('fade-out');
        }
    };
    const toggleDisplayImagesMode = async () => {
        setDisplayImages((prev) => !prev);
       
    };

    const startVoiceSession = async () => {
        if (!isConnected || !selectedAgent) return;

         startRecording();
         initializeAudioContext();
        
        connection.current.invoke("StartVoiceService", sessionId, "Web");
        
        
        console.log("Voice service started");
        
    };

    const startRecording = async () => {
        setIsRecording(true);

        try {
            console.log(selectedAgent.voice);
            var sample = 16000;
            if (selectedAgent.voice.startsWith("Realtime")) {
                sample = 24000;
            }
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const audioContext = new AudioContext({ sampleRate: sample }); // Set sample rate to 16kHz
            const source = audioContext.createMediaStreamSource(stream);

            const processor = audioContext.createScriptProcessor(16384, 1, 1); // Mono audio

            source.connect(processor);
            processor.connect(audioContext.destination);

            processor.onaudioprocess =  (event) => {
                const inputBuffer = event.inputBuffer.getChannelData(0); // Mono audio data
                const audioChunk = convertToLinear16(inputBuffer); // Convert to Linear16

                const base64Chunk = arrayBufferToBase64(audioChunk);
                 sendAudioChunk(base64Chunk);
            };

            // Save references for cleanup
            mediaRecorderRef.current = { processor, audioContext, stream };
        } catch (error) {
            console.error("Error starting recording:", error);
        }
    };

    const stopRecording = async () => {
        if (mediaRecorderRef.current) {
            const { processor, audioContext, stream } = mediaRecorderRef.current;
            processor.disconnect();
            audioContext.close();
            stream.getTracks().forEach((track) => track.stop());
        }
        audioContextRef.current?.close();
        await connection.current.invoke("StopVoiceService", sessionId);
        console.log("Voice service stopped");
        setIsRecording(false);

    };

    const sendAudioChunk = async (audioChunk) => {
        try {
            if (sessionId && connection.current) {
                await connection.current.invoke("SendAudioChunk", sessionId, audioChunk);
                console.log("Audio chunk sent");
            }
        } catch (error) {
            console.error("Error sending audio chunk:", error);
        }
    };

    // Convert Float32Array to Linear16 PCM
    const convertToLinear16 = (inputBuffer) => {
        const buffer = new ArrayBuffer(inputBuffer.length * 2); // 16-bit PCM = 2 bytes per sample
        const view = new DataView(buffer);

        for (let i = 0; i < inputBuffer.length; i++) {
            let sample = inputBuffer[i];
            if (sample > 1) sample = 1;
            else if (sample < -1) sample = -1;

            sample = sample * 32767; // Scale to 16-bit range
            view.setInt16(i * 2, sample, true); // Little-endian
        }

        return buffer;
    };

    // Convert ArrayBuffer to Base64
    const arrayBufferToBase64 = (buffer) => {
        let binary = '';
        const bytes = new Uint8Array(buffer);
        for (let i = 0; i < bytes.length; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        return window.btoa(binary);
    };

    const initializeAudioContext = () => {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)({
            sampleRate: 24000,
        });
        console.log("Initialized AudioContext for playback.");
    };

    const handleIncomingChunk = async (base64Chunk) => {
        try {
            const audioBuffer = decodeBase64ToArrayBuffer(base64Chunk);
            const wavAudio = addWavHeader(audioBuffer); // Dodaj nagłówek WAV

            // Dekodowanie audio i dodanie do kolejki
            const decodedBuffer = await audioContextRef.current.decodeAudioData(wavAudio);
            audioBufferQueueRef.current.push(decodedBuffer);

            playNextInQueue(); // Spróbuj odtworzyć kolejkę
        } catch (error) {
            console.error("Error processing audio chunk:", error);
        }
    };
    const bargeIn = async () => {
        // Sprawdź, czy cokolwiek jest odtwarzane
        if (isPlayingRef.current && audioSourceRef.current) {
            console.log("🛑 Stopping current audio...");
            audioSourceRef.current.stop(); // Stop aktywnego dźwięku
            audioSourceRef.current.disconnect();
            audioSourceRef.current = null;
            isPlayingRef.current = false;
        }

        // Wyczyść kolejkę audio
        if (audioBufferQueueRef.current.length > 0) {
            audioBufferQueueRef.current = [];
            console.log("🔄 Cleared audio queue.");
        } else {
            console.log("ℹ️ No audio to interrupt.");
        }
    };


    const playNextInQueue = () => {
        if (isPlayingRef.current || audioBufferQueueRef.current.length === 0) {
            return; // Nie odtwarzaj, jeśli coś już jest grane lub brak buforów
        }

        isPlayingRef.current = true;

        // Łączenie wszystkich buforów w jeden
        const totalDuration = audioBufferQueueRef.current.reduce(
            (acc, buffer) => acc + buffer.duration,
            0
        );
        const sampleRate = audioContextRef.current.sampleRate;
        const numberOfChannels = audioBufferQueueRef.current[0].numberOfChannels;

        const totalSamples = Math.ceil(totalDuration * sampleRate);
        const mixedBuffer = audioContextRef.current.createBuffer(
            numberOfChannels,
            totalSamples,
            sampleRate
        );

        let offset = 0;
        audioBufferQueueRef.current.forEach((buffer) => {
            for (let channel = 0; channel < numberOfChannels; channel++) {
                mixedBuffer.copyToChannel(buffer.getChannelData(channel), channel, offset);
            }
            offset += buffer.length;
        });

        audioBufferQueueRef.current = []; // Wyczyść kolejkę po połączeniu

        // Odtwarzanie połączonego bufora
        const source = audioContextRef.current.createBufferSource();
        source.buffer = mixedBuffer;
        source.connect(audioContextRef.current.destination);
        source.onended = () => {
            isPlayingRef.current = false; // Odtwarzanie zakończone
            playNextInQueue(); // Spróbuj odtworzyć kolejne dane
        };
        audioSourceRef.current = source;
        source.start(0);
        console.log("Playing audio buffer.");
    };

    const decodeBase64ToArrayBuffer = (base64) => {
        const binaryString = atob(base64);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }
        return bytes.buffer;
    };

    const addWavHeader = (audioBuffer, sampleRate = 24000, channels = 1) => {
        const bufferLength = audioBuffer.byteLength;
        const header = new ArrayBuffer(44);
        const view = new DataView(header);

        /* RIFF header */
        writeString(view, 0, "RIFF");
        view.setUint32(4, 36 + bufferLength, true);
        writeString(view, 8, "WAVE");
        writeString(view, 12, "fmt ");
        view.setUint32(16, 16, true); // Format chunk length
        view.setUint16(20, 1, true); // PCM format
        view.setUint16(22, channels, true); // Channels
        view.setUint32(24, sampleRate, true); // Sample rate
        view.setUint32(28, sampleRate * channels * 2, true); // Byte rate
        view.setUint16(32, channels * 2, true); // Block align
        view.setUint16(34, 16, true); // Bits per sample
        writeString(view, 36, "data");
        view.setUint32(40, bufferLength, true);

        const wavBuffer = new Uint8Array(44 + bufferLength);
        wavBuffer.set(new Uint8Array(header), 0);
        wavBuffer.set(new Uint8Array(audioBuffer), 44);

        return wavBuffer.buffer;
    };

    const writeString = (view, offset, string) => {
        for (let i = 0; i < string.length; i++) {
            view.setUint8(offset + i, string.charCodeAt(i));
        }
    };


    useEffect(() => {
        if (chatMessagesRef.current) {
            chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
        }
    }, [messages[selectedAgent?.guid]]);

    useEffect(() => {
        if (machines[0] && sessionId) {
            connectComputer();
        
        }
    }, [sessionId]);

   

    return (
        <div className="chat-area">
            {/* Messages */}
            
            <div className="chat-messages" ref={chatMessagesRef}>
                {selectedAgent && messages[selectedAgent.guid]?.length > 0 ? (
                    messages[selectedAgent.guid].map((msg, index) => (
                        <div key={index} className={`message ${msg.type}`}>
                            {msg.type === "image" ? (
                                <>
                                <p>Vision image</p>
                                <img
                                    src={msg.message.startsWith("data:image") ? msg.message : `data:image/png;base64,${msg.message}`} // The message contains the image URL or base64 data
                                    alt="Sent image"
                                    style={{
                                        maxWidth: "300px",
                                        maxHeight: "300px",
                                        borderRadius: "8px",
                                        display: "block",
                                        margin: "5px 0",
                                    }}
                                    onClick={() => handleImageClick(msg.message)}
                                    />
                                </>
                            ) : (
                                <>{msg.message}</> // Default text message rendering
                            )}
                        </div>
                    ))
                ) : (
                    <div>No messages yet</div>
                )}
            </div>

            {/* Input */}
            <div className="image-attachment-container">
                <div className="image-preview-wrapper">
                    {imagesAtt.map((img, idx) => (
                        <div key={idx} className="image-thumb">
                            <img src={img.url} alt={`preview-${idx}`} />
                            <button className="remove-btn" onClick={() => handleRemoveImage(idx)}>✖</button>
                        </div>
                    ))}
                </div>

                <button className="upload-btn" onClick={() => fileInputRef.current.click()}>
                    +
                </button>
                <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: "none" }}
                    accept="image/*"
                    multiple
                    onChange={handleImageChange}
                />
            </div>
            <div className="chat-input">
                <input
                    ref={messageInputRef}
                    type="text"
                    placeholder={
                        selectedAgent
                            ? `Chat with ${selectedAgent.name}...`
                            : "Select an agent to start chatting"
                    }
                    onKeyPress={(e) => e.key === "Enter" && sendMessage()}
                />
                <Box display="flex" alignItems="center" gap={2}>
                    <Tooltip title="Start Voice Chat">
                        <IconButton
                            color="primary"
                            onClick={startVoiceSession}
                            disabled={isRecording}
                        >
                            <MicIcon />
                        </IconButton>
                    </Tooltip>

                    <Tooltip title="Stop Voice Chat">
                        <IconButton
                            color="error"
                            onClick={stopRecording}
                            disabled={!isRecording}
                        >
                            <StopIcon />
                        </IconButton>
                    </Tooltip>

                    
                </Box>
               
                <Tooltip title="Send Message">
                    <Button
                        color="primary"
                        onClick={sendMessage}
                    ><SendIcon onClick={sendMessage} /></Button>
                </Tooltip>
            </div>
            {/* Modal for Enlarged Image */}
            {selectedImage && (
                <div
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        backgroundColor: "rgba(0, 0, 0, 0.8)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 1000,
                    }}
                    onClick={closeModal} // Close modal when background is clicked
                >
                    <img
                        src={selectedImage.startsWith("data:image") ? selectedImage : `data:image/png;base64,${selectedImage}`}
                        alt="Enlarged"
                        style={{
                            maxWidth: "90%",
                            maxHeight: "90%",
                            borderRadius: "8px",
                            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
                        }}
                    />
                </div>)}

        </div>
    );
}
